import { NgModule, Optional, Provider, SkipSelf } from '@angular/core';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';

import { StoreModule } from '@ngrx/store';
import { AuthModule, LogLevel } from 'angular-auth-oidc-client';

import { environment } from '@environments/environment';
import { EffectsModule } from '@ngrx/effects';
import { AuthSyncService } from '@app/auth/auth-sync.service';
import { getIsSiriusEnabled } from '@app/common/sirius-util';
import { AUTH_FEATURE_NAME, reducer } from './store';
import { getInitialAuthState } from './store/reducers';
import { PlAuthService, PlAuthInterceptor } from './services';
import { UserAuthGuardService } from './guards';
import { PlAuthEffects } from './store/effects';

const isSiriusEnabled = getIsSiriusEnabled();

const isSiriusEnabledProviderList: Provider[] = [
  PlAuthService,
  PlAuthInterceptor,
  { provide: HTTP_INTERCEPTORS, useClass: PlAuthInterceptor, multi: true },
];

const authEnabledProviderList: Provider[] = [
  PlAuthService,
  PlAuthInterceptor,
  UserAuthGuardService,
  { provide: HTTP_INTERCEPTORS, useClass: PlAuthInterceptor, multi: true },
];

const PROVIDERS: Provider[] = isSiriusEnabled
  ? isSiriusEnabledProviderList
  : authEnabledProviderList;

@NgModule({
  declarations: [],
  imports: [
    HttpClientModule,
    RouterModule,
    StoreModule.forFeature(AUTH_FEATURE_NAME, reducer, {
      initialState: getInitialAuthState,
    }),
    EffectsModule.forFeature(
      [!isSiriusEnabled && PlAuthEffects].filter(Boolean)
    ),
    AuthModule.forRoot({
      config: [
        isSiriusEnabled && {
          configId: 'kanga-local',
          authority: `${environment.sirius_url}/oidc`,
          redirectUrl: `${window.location.origin}/auth/callback`,
          authWellknownEndpointUrl: `${environment.sirius_url}/oidc/.well-known/openid-configuration`,
          postLogoutRedirectUri: `${window.location.origin}/auth/logged-out`,
          secureRoutes: [`${environment.sirius_url}/oidc`],
          postLoginRoute: '/',
          clientId: 'kanga-local',
          scope:
            'openid profile email offline_access phone address pl:extended_profile',
          responseType: 'code',
          logLevel: LogLevel.Debug,
          useRefreshToken: true,
          autoCleanStateAfterAuthentication: true,
          renewTimeBeforeTokenExpiresInSeconds: 30,
          refreshTokenRetryInSeconds: 3,
          ignoreNonceAfterRefresh: false, // Intentionally false, as we need to check nonce after refresh
        },
      ].filter(Boolean),
    }),
  ],
  providers: [...PROVIDERS, AuthSyncService],
})
export class PlAuthModule {
  constructor(
    @Optional() @SkipSelf() authModule: PlAuthModule,
    private authSyncService: AuthSyncService
  ) {
    if (authModule) {
      throw new TypeError(`PlAuthModule is imported twice.`);
    }
  }
}
